'use client';

import React from 'react';

// Import only the icons actually used in the codebase (tree-shakeable)
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsUpDownIcon,
  ArchiveBoxIcon,
  Bars3Icon,
  CalendarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FolderIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  FunnelIcon,
  HomeIcon,
  InformationCircleIcon,
  LightBulbIcon,
  LinkIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PencilIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  Squares2X2Icon,
  TableCellsIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Heroicons v2 exports ForwardRefExoticComponent; use a compatible base type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HeroIcon = React.ComponentType<any>;

/** Curated map of outline icons used across the codebase. Add new icons here as needed. */
const outlineIcons: Record<string, HeroIcon> = {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsUpDownIcon,
  ArchiveBoxIcon,
  Bars3Icon,
  CalendarIcon,
  ChartBarIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FolderIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  FunnelIcon,
  HomeIcon,
  InformationCircleIcon,
  LightBulbIcon,
  LinkIcon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PencilIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  Squares2X2Icon,
  TableCellsIcon,
  TrashIcon,
  XMarkIcon,
};

type IconVariant = 'outline' | 'solid';

interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'ref'> {
  name: string; // dynamic icon name key
  variant?: IconVariant;
  size?: number;
  disabled?: boolean;
}

function Icon({
    name,
    variant = 'outline',
    size = 24,
    className = '',
    onClick,
    disabled = false,
    ...props
}: IconProps) {
    // Currently only outline icons are used; solid support can be added later
    const iconSet = variant === 'solid' ? outlineIcons : outlineIcons;
    const IconComponent = iconSet[name];

    if (!IconComponent) {
        return (
            <QuestionMarkCircleIcon data-component-id="src/components/ui/AppIcon.tsx:127:12" data-component-path="src/components/ui/AppIcon.tsx" data-component-line="127" data-component-end-line="133" data-component-file="AppIcon.tsx" data-component-name="QuestionMarkCircleIcon" data-component-content="%7B%22elementName%22%3A%22QuestionMarkCircleIcon%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
                width={size}
                height={size}
                className={`text-gray-400 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
                onClick={disabled ? undefined : onClick}
                {...props}
            />
        );
    }

    return (
        <IconComponent data-component-id="src/components/ui/AppIcon.tsx:138:8" data-component-path="src/components/ui/AppIcon.tsx" data-component-line="138" data-component-end-line="144" data-component-file="AppIcon.tsx" data-component-name="IconComponent" data-component-content="%7B%22elementName%22%3A%22IconComponent%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            width={size}
            height={size}
            className={`${disabled ? 'opacity-50 cursor-not-allowed' : onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
            onClick={disabled ? undefined : onClick}
            {...props}
        />
    );
}

export default Icon; 
